'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FieldWrapper,
  Form,
  FormField,
  Icon,
  PhoneInput,
  TextInput,
  toast,
  Typography,
} from '@polyseme/design-system';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { DynamicFontTypography } from '~/landing/components';
import { api } from '~/landing/trpc/client';

const contactFormSchema = z.object({
  firstname: z.string().min(1),
  lastname: z.string().min(1),
  email: z.string().email(),
  phoneNumber: z.string().min(1),
  company: z.string().min(1),
});
type ContactForm = z.infer<typeof contactFormSchema>;

export const ContactTab = () => {
  const t = useTranslations('landing.retraite.contactCard.tabs.contactMe');
  const tCommon = useTranslations('landing.germen');
  const tRequest = useTranslations('landing.common.contactEmail');
  const [displaySuccessTab, setDisplaySuccessTab] = useState(false);

  const form = useForm<ContactForm>({
    resolver: zodResolver(contactFormSchema),
  });

  const { isPending, mutateAsync: sendContactEmail } =
    api.email.sendContactEmail.useMutation();

  const onSubmit = async (data: ContactForm) => {
    try {
      await sendContactEmail({
        ...data,
        requestType: tRequest('requestType.simulator'),
        app: tRequest('app.germen'),
      });
      setDisplaySuccessTab(true);
    } catch {
      toast({
        title: t('error.title'),
        description: t('error.description'),
        variant: 'destructive',
      });
    }
  };

  if (displaySuccessTab) {
    return (
      <div className="grid h-[560px] place-items-center">
        <div className="flex flex-col items-center">
          <div className="bg-brand-accent text-brand mb-4 flex size-14 items-center justify-center rounded-full">
            <Icon className="size-6" name="file-check-2" />
          </div>
          <Typography className="mb-2" variant="title-3">
            {t('success.title')}
          </Typography>
          <Typography className="whitespace-pre-wrap text-center">
            {t('success.subtitle')}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DynamicFontTypography className="text-center text-title2 lg:text-title2desktop">
        {tCommon('contactCard.title')}
      </DynamicFontTypography>
      <div className="mx-auto w-full max-w-2xl rounded-lg bg-card p-6 shadow-sm">
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="firstname"
                render={({ field }) => (
                  <FieldWrapper label={tCommon('contactCard.form.firstname')}>
                    <TextInput autoComplete="given-name" {...field} />
                  </FieldWrapper>
                )}
              />

              <FormField
                control={form.control}
                name="lastname"
                render={({ field }) => (
                  <FieldWrapper label={tCommon('contactCard.form.lastname')}>
                    <TextInput autoComplete="family-name" {...field} />
                  </FieldWrapper>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FieldWrapper label={tCommon('contactCard.form.email')}>
                    <TextInput autoComplete="email" type="email" {...field} />
                  </FieldWrapper>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FieldWrapper label={tCommon('contactCard.form.phoneNumber')}>
                    <PhoneInput {...field} />
                  </FieldWrapper>
                )}
              />

              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FieldWrapper label={tCommon('contactCard.form.company')}>
                    <TextInput autoComplete="organization" {...field} />
                  </FieldWrapper>
                )}
              />
            </div>

            <Button
              className="w-full"
              isLoading={isPending}
              size="lg"
              type="submit"
            >
              <Icon className="mr-2 size-5" name="phone" />
              {tCommon('contactCard.submit')}
            </Button>

            <div className="flex items-start">
              <Icon
                className="mt-0.5 text-muted-foreground"
                name="info"
                size="xs"
              />
              <Typography
                className="text-center text-muted-foreground"
                variant="caption"
              >
                {tCommon('contactCard.legal')}
              </Typography>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
